import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Calendar, ChefHat, Heart, Edit } from "lucide-react";
import { useMyRecipes } from "../../hooks/useMyRecipes";
import type { StandardRecipeV1 } from "../../types/StandardRecipe";
import { useLikedRecipes } from "../../hooks/useLikedRecipes";
import { ContextRecipeCard } from "../components/recipe/ContextRecipeCard";
import { RecipeDetailModal } from "../components/discover/RecipeDetailModal";
import { Button } from "../components/ui/button";
import {
  ProgressiveSkeleton,
  PROGRESSIVE_SKELETONS,
} from "../components/ui/progressive-skeleton";
import { AnimatedSkeleton } from "../components/ui/animated-skeleton";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Grid3X3, List, SortAsc, SortDesc, Filter, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

type TabKey = "all" | "favorites" | "custom" | "personalized";
type SortOption = "recent" | "alphabetical" | "difficulty" | "cuisine";
type ViewMode = "grid" | "list";

function MyRecipesPage() {
  const navigate = useNavigate();
  const { all, favorites, custom, personalized, isLoading } = useMyRecipes();
  const { unlikeRecipe, totalLiked } = useLikedRecipes();

  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<TabKey>("all");
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [filterCuisine, setFilterCuisine] = useState<string>("");
  const [filterDifficulty, setFilterDifficulty] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRecipe, setModalRecipe] = useState<StandardRecipeV1 | null>(null);

  // Get recipes for current tab
  const lists = { all, favorites, custom, personalized } as const;
  const currentRecipes: StandardRecipeV1[] = lists[tab] as StandardRecipeV1[];

  // Filter and sort recipes
  const filteredAndSortedRecipes = useMemo<StandardRecipeV1[]>(() => {
    let filtered: StandardRecipeV1[] = currentRecipes;

    // Search filter
    if (query.trim()) {
      filtered = filtered.filter(
        (recipe) =>
          recipe.title.toLowerCase().includes(query.toLowerCase()) ||
          recipe.cuisine?.toLowerCase().includes(query.toLowerCase()),
      );
    }

    // Cuisine filter
    if (filterCuisine) {
      filtered = filtered.filter((recipe) => recipe.cuisine === filterCuisine);
    }

    // Difficulty filter
    if (filterDifficulty) {
      filtered = filtered.filter(
        (recipe) => recipe.difficulty === filterDifficulty,
      );
    }

    // Sort recipes
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return (
            new Date(b.likedAt || b.createdAt || "").getTime() -
            new Date(a.likedAt || a.createdAt || "").getTime()
          );
        case "alphabetical":
          return a.title.localeCompare(b.title);
        case "difficulty":
          const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
          const aOrder =
            difficultyOrder[a.difficulty as keyof typeof difficultyOrder] || 4;
          const bOrder =
            difficultyOrder[b.difficulty as keyof typeof difficultyOrder] || 4;
          return aOrder - bOrder;
        case "cuisine":
          return (a.cuisine || "").localeCompare(b.cuisine || "");
        default:
          return 0;
      }
    });

    return sorted;
  }, [currentRecipes, query, sortBy, filterCuisine, filterDifficulty]);

  // Get unique cuisines and difficulties for filters
  const uniqueCuisines = useMemo<string[]>(() => {
    const cuisines = currentRecipes
      .map((recipe: StandardRecipeV1) => recipe.cuisine)
      .filter(Boolean) as string[];
    const uniq = cuisines.filter(
      (cuisine: string, index: number, arr: string[]) =>
        arr.indexOf(cuisine) === index,
    );
    return uniq.sort();
  }, [currentRecipes]);

  const uniqueDifficulties = useMemo<string[]>(() => {
    const diffs = currentRecipes
      .map((recipe: StandardRecipeV1) => recipe.difficulty)
      .filter(Boolean) as string[];
    const uniq = diffs.filter(
      (difficulty: string, index: number, arr: string[]) =>
        arr.indexOf(difficulty) === index,
    );
    return uniq.sort();
  }, [currentRecipes]);

  // Stable skeletons for loading state
  const gridSkeletons = useMemo(() => PROGRESSIVE_SKELETONS.recipeGrid(8), []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <AnimatedSkeleton variant="shimmer" className="h-8 w-72 rounded-md" />
        <ProgressiveSkeleton
          skeletons={gridSkeletons}
          duration={1500}
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        />
      </div>
    );
  }

  const handleUnlike = (recipeId: string) => {
    unlikeRecipe(recipeId);
  };

  const handleEditRecipe = (recipeId: string) => {
    // Navigate to edit page for custom recipes
    navigate(`/recipes/${recipeId}/edit`);
  };

  const handleViewDetails = (recipeId: string) => {
    // Open in-page modal instead of navigating to recipe route
    const r = filteredAndSortedRecipes.find(r => r.id === recipeId);
    if (r) {
      setModalRecipe(r as any);
      setModalOpen(true);
    } else {
      navigate(`/recipe/${recipeId}`);
    }
  };

  const handleClearFilters = () => {
    setQuery("");
    setFilterCuisine("");
    setFilterDifficulty("");
    setSortBy("recent");
  };

  const getEmptyStateContent = () => {
    switch (tab) {
      case "favorites":
        return {
          icon: Heart,
          title: "No favorite recipes yet",
          description:
            "Start discovering recipes and tap the heart icon to save your favorites here.",
          actionText: "Discover Recipes",
          actionHref: "/discover",
        };
      case "custom":
        return {
          icon: Edit,
          title: "No custom recipes yet",
          description:
            "Create your own personalized recipes with ingredients and instructions.",
          actionText: "Create Recipe",
          actionHref: "/recipes/create",
        };
      case "personalized":
        return {
          icon: ChefHat,
          title: "No personalized recommendations yet",
          description:
            "Complete your taste profile to get AI-powered recipe recommendations tailored just for you.",
          actionText: "Update Profile",
          actionHref: "/profile",
        };
      default:
        return {
          icon: Search,
          title: "No recipes found",
          description: query
            ? "Try adjusting your search or filters to find more recipes."
            : "Your recipe collection will appear here once you start adding favorites, creating custom recipes, or receiving personalized recommendations.",
          actionText: query ? "Clear Filters" : "Discover Recipes",
          actionHref: query ? undefined : "/discover",
        };
    }
  };

  return (
    <div>
        {/* Local recipe detail modal (keeps My Recipes in the background) */}
        <RecipeDetailModal
          recipe={modalRecipe as any}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          preview={null}
          previewLoading={false}
          previewError={null}
        />
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <h1 className="flex items-center gap-3 text-3xl font-bold text-martinique">
                <ChefHat className="h-8 w-8 text-coral" />
                My Recipes
              </h1>
              <p className="mt-2 text-martinique/70">
                All your recipes in one place - favorites, custom creations, and
                personalized recommendations
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filters</span>
              </Button>
              <Button
                onClick={() =>
                  setViewMode(viewMode === "grid" ? "list" : "grid")
                }
                variant="outline"
                className="flex items-center gap-2"
              >
                {viewMode === "grid" ? (
                  <List className="h-4 w-4" />
                ) : (
                  <Grid3X3 className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">
                  {viewMode === "grid" ? "List" : "Grid"}
                </span>
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-martinique/50" />
              <Input
                placeholder="Search your recipes..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <Card className="mt-6">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {/* Sort */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-martinique">
                      Sort by
                    </label>
                    <Select
                      value={sortBy}
                      onValueChange={(value: SortOption) => setSortBy(value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recent">Recently Added</SelectItem>
                        <SelectItem value="alphabetical">
                          Alphabetical
                        </SelectItem>
                        <SelectItem value="difficulty">Difficulty</SelectItem>
                        <SelectItem value="cuisine">Cuisine</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Cuisine Filter */}
                  {uniqueCuisines.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-martinique">
                        Cuisine
                      </label>
                      <Select
                        value={filterCuisine}
                        onValueChange={setFilterCuisine}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All cuisines" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All cuisines</SelectItem>
                          {uniqueCuisines.map((cuisine) => (
                            <SelectItem key={cuisine} value={cuisine!}>
                              {cuisine}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Difficulty Filter */}
                  {uniqueDifficulties.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-martinique">
                        Difficulty
                      </label>
                      <Select
                        value={filterDifficulty}
                        onValueChange={setFilterDifficulty}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All levels" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All levels</SelectItem>
                          {uniqueDifficulties.map((difficulty) => (
                            <SelectItem key={difficulty} value={difficulty!}>
                              {difficulty}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Active Filters */}
                {(query ||
                  filterCuisine ||
                  filterDifficulty ||
                  sortBy !== "recent") && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-martinique/70">
                      Active filters:
                    </span>
                    {query && (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        Search: {query}
                        <button onClick={() => setQuery("")}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    {filterCuisine && (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        Cuisine: {filterCuisine}
                        <button onClick={() => setFilterCuisine("")}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    {filterDifficulty && (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        Difficulty: {filterDifficulty}
                        <button onClick={() => setFilterDifficulty("")}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    {sortBy !== "recent" && (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        Sort: {sortBy}
                        <button onClick={() => setSortBy("recent")}>
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )}
                    <Button
                      onClick={handleClearFilters}
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                    >
                      Clear all
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          <TabsList className="mb-6 grid w-full grid-cols-4 lg:inline-flex lg:w-auto">
            <TabsTrigger value="all" className="flex items-center gap-2">
              All Recipes
              <Badge variant="secondary" className="h-5 px-2 text-xs">
                {all.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="favorites" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Favorites
              <Badge variant="secondary" className="h-5 px-2 text-xs">
                {favorites.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="custom" className="flex items-center gap-2">
              <Edit className="h-4 w-4" />
              Custom
              <Badge variant="secondary" className="h-5 px-2 text-xs">
                {custom.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger
              value="personalized"
              className="flex items-center gap-2"
            >
              <ChefHat className="h-4 w-4" />
              Personalized
              <Badge variant="secondary" className="h-5 px-2 text-xs">
                {personalized.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-0">
            {isLoading ? (
              <div className="py-20 text-center">
                <div className="animate-pulse space-y-4">
                  <div className="mx-auto h-8 w-48 rounded bg-lynx"></div>
                  <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="h-64 rounded-lg bg-lynx"></div>
                    ))}
                  </div>
                </div>
              </div>
            ) : filteredAndSortedRecipes.length === 0 ? (
              /* Empty State */
              <Card className="py-16 text-center">
                <CardContent>
                  {(() => {
                    const emptyState = getEmptyStateContent();
                    const Icon = emptyState.icon;
                    return (
                      <>
                        <Icon className="mx-auto mb-6 h-16 w-16 text-martinique/30" />
                        <h3 className="mb-2 text-xl font-semibold text-martinique">
                          {emptyState.title}
                        </h3>
                        <p className="mx-auto mb-6 max-w-md text-martinique/70">
                          {emptyState.description}
                        </p>
                        {emptyState.actionHref && (
                          <Button asChild>
                            <a
                              href={emptyState.actionHref}
                              className="flex items-center gap-2"
                            >
                              {emptyState.actionText}
                            </a>
                          </Button>
                        )}
                        {query && !emptyState.actionHref && (
                          <Button
                            onClick={handleClearFilters}
                            variant="outline"
                          >
                            {emptyState.actionText}
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            ) : (
              /* Recipe Grid/List */
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    : "space-y-4"
                }
              >
                {filteredAndSortedRecipes.map((recipe, index) => (
                  <ContextRecipeCard
                    key={`${recipe.origin}:${recipe.id}`}
                    context="myRecipes"
                    recipe={recipe}
                    variantOverride={
                      viewMode === "grid" ? "detailed" : "compact"
                    }
                    imageFetchPriority={index < 4 ? "high" : "auto"}
                    isLiked={recipe.origin === "favorite"}
                    onLike={() => handleUnlike(recipe.id)}
                    onEdit={
                      recipe.origin === "custom"
                        ? () => handleEditRecipe(recipe.id)
                        : undefined
                    }
                    onView={handleViewDetails}
                    featuresOverride={{ rating: false, tags: false }}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
  );
}

export default MyRecipesPage;
export { MyRecipesPage };
